import React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import { useSwipeDeck } from "./useSwipeDeck";
import { ImageTextCard } from "./cards/ImageTextCard";
import { VideoCard } from "./cards/VideoCard";
import { VideoEmbedCard } from "./cards/VideoEmbedCard";
import { PollCard } from "./cards/PollCard";
import { TriviaCard } from "./cards/TriviaCard";
import { CompletionScreen } from "./CompletionScreen";
import { useNotifyMobileModal } from "../useNotifyMobileModal";

/**
 * Fullscreen swipe-deck player. Mounts as a fixed overlay above the rest
 * of the app. Phone-shaped on desktop, edge-to-edge on mobile.
 *
 * Architecture:
 *   - useSwipeDeck owns data + mutators (deck, responses, completion)
 *   - Embla carousel (vertical axis) owns slide state and gesture handling
 *   - Each card component receives `isActive`, the user's existing response
 *     (if any), and `onAdvance` so it can self-pace its auto-advance
 *   - The slide *after* the last card is the CompletionScreen; reaching it
 *     triggers submitCompletion. Server-side validation rejects if
 *     interactive cards are unanswered, in which case we scroll to the
 *     first unanswered one.
 *
 * Loaded via React.lazy from CompleteTrack.js so the framer-motion +
 * embla-carousel + canvas-confetti bundle (~66KB gzipped) only downloads
 * when an employee actually opens a deck.
 */

// Auto-advance ("auto-scroll") is disabled for all viewers — cards never
// move on their own, neither on the timed text/video window nor after a
// poll/quiz answer. The viewer always swipes/clicks to advance. This is the
// single gate: handleAdvance is the cards' only `onAdvance` callback (manual
// swipe, keyboard arrows, and click-nav all call embla directly), so
// short-circuiting it turns off every automatic transition while leaving
// manual navigation untouched. Flip to true to restore the TikTok-style
// self-paced behavior.
const AUTO_ADVANCE_ENABLED = false;

export default function SwipeDeckPlayer({
  deckUuid,
  sectionUuid,
  companyId,
  employeeID,
  language,
  onClose,
  onCompleted,
  previewMode = false,
  previewDeck = null,
  publicShareToken = null,
}) {
  // Player is rendered only when open; notify mobile chrome so the native X
  // hides while the deck overlay is up.
  useNotifyMobileModal(true);

  const {
    deck,
    responsesByCardUuid,
    isLoading,
    error,
    submitResponse,
    submitCompletion,
    refreshCardMedia,
    isCardMediaStale,
  } = useSwipeDeck({
    deckUuid,
    sectionUuid,
    companyId,
    employeeID,
    language,
    previewMode,
    previewDeck,
    publicShareToken,
  });

  // Public share page: there's no app shell to close back to, so hide the X and
  // turn the completion screen's primary action into Replay.
  const isPublic = !!publicShareToken;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: "y",
    loop: false,
    containScroll: "trimSnaps",
    skipSnaps: false,
    duration: 25, // tighter than default for a snappier swipe feel
  });

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  // Mute is shared across every video card in the deck: unmuting one clip
  // keeps sound on as the viewer swipes through the rest (they shouldn't have
  // to re-unmute each clip). Starts muted for autoplay-policy compliance.
  const [isMuted, setIsMuted] = React.useState(true);
  const handleToggleMute = React.useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);
  // Playback progress (0–1) of the active VIDEO card, so its segment in the
  // top progress bar fills in real time (Instagram Stories style) and the
  // viewer can sense the clip's length. Reset whenever the active card changes.
  const [videoProgress, setVideoProgress] = React.useState(0);
  React.useEffect(() => {
    setVideoProgress(0);
  }, [selectedIndex]);

  const cards = React.useMemo(() => deck?.cards || [], [deck]);
  const totalCards = cards.length;
  // Completion screen is rendered as the slide AFTER the last card. Embla
  // therefore has `totalCards + 1` slides, and the progress bar mirrors
  // that with one chunk per slide (cards + the celebration screen).
  // Empty decks (totalCards === 0) collapse to a single completion-only
  // slide — `selectedIndex >= totalCards` is then 0 >= 0 = true, so the
  // user is "on the completion screen" the moment the player mounts and
  // the section completion request still gets fired (zero-card decks
  // are valid saves and back-end completion checks treat them as
  // required content; gating on totalCards > 0 here would leave those
  // sections permanently incomplete).
  //
  // Important: also gate on `!isLoading && deck`. During the initial
  // fetch, `cards` is `[]` and `totalCards` is 0, which would make
  // `selectedIndex >= totalCards` evaluate to `0 >= 0 = true` and fire
  // the completion submit before the deck has even loaded. That
  // premature POST returns 4xx (no responses yet) and the catch path
  // would surface a false-positive error toast on every deck open. We
  // can't know whether the deck is actually empty until the fetch
  // resolves.
  const isDeckReady = !isLoading && !!deck;
  const isOnCompletionScreen = isDeckReady && selectedIndex >= totalCards;

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Embla wiring

  // Tracks the previously-committed snap so the select handler can detect
  // forward vs. backward movement. Used by the per-card gate below.
  const previousSnapRef = React.useRef(0);

  React.useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => {
      const newIdx = emblaApi.selectedScrollSnap();
      const prevIdx = previousSnapRef.current;
      // Per-card forward gate: if the user just tried to advance past an
      // interactive card (trivia/poll) they haven't answered, snap back
      // to it. Mirrors CompleteQuizModal's per-question lock and matches
      // the backend's completion contract — `post_learning_swipe_deck_
      // completion` rejects any deck with missing responses on
      // interactive cards, so without this gate users could swipe to
      // the completion screen only to hit a rejection there. Backward
      // movement (newIdx < prevIdx) is always allowed so reviewing
      // earlier cards still works.
      if (newIdx > prevIdx) {
        const leavingCard = cardsRef.current[prevIdx];
        const isInteractive =
          leavingCard?.card_type === "quiz" ||
          leavingCard?.card_type === "poll";
        const hasResponse =
          !!responsesByCardUuidRef.current[leavingCard?.uuid];
        if (isInteractive && !hasResponse) {
          emblaApi.scrollTo(prevIdx);
          // Fixed toastId dedupes rapid swipe attempts onto a single
          // toast instance instead of stacking one per try.
          const tt = tOuterRef.current;
          toast.info(
            tt(
              "Answer to continue",
              "Responde para continuar",
              "答题后继续",
            ),
            { toastId: "swipe-deck-answer-required", autoClose: 1500 },
          );
          return;
        }
      }
      previousSnapRef.current = newIdx;
      setSelectedIndex(newIdx);
    };
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Embla measurement is computed off the parent height. When the deck
  // finishes loading and slides actually render, we need to tell Embla
  // to re-measure or its slide heights will stay at 0.
  React.useEffect(() => {
    if (emblaApi && totalCards > 0) emblaApi.reInit();
  }, [emblaApi, totalCards]);

  // Auto-advance disabled for all viewers — see AUTO_ADVANCE_ENABLED above.
  const handleAdvance = React.useCallback(() => {
    if (!AUTO_ADVANCE_ENABLED) return;
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  React.useEffect(() => {
    if (!cards.length) return undefined;
    const nearbyCardUuids = [selectedIndex - 1, selectedIndex, selectedIndex + 1]
      .filter((idx) => idx >= 0 && idx < cards.length)
      .map((idx) => cards[idx].uuid);

    for (const cardUuid of nearbyCardUuids) {
      refreshCardMedia(cardUuid).catch(() => {});
    }
    return undefined;
  }, [cards, selectedIndex, refreshCardMedia]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Keyboard navigation

  React.useEffect(() => {
    const handleKey = (e) => {
      // While a read-more sheet is open (portaled into the frame), let it own
      // the keyboard: don't advance/scroll the deck or close the player out
      // from under the reader. The sheet handles its own Escape + arrow scroll.
      const frame = document.getElementById("swipe-deck-frame");
      if (frame && frame.querySelector("[data-deck-sheet]")) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onClose && onClose();
      } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        if (emblaApi) emblaApi.scrollNext();
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        if (emblaApi) emblaApi.scrollPrev();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [emblaApi, onClose]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Body scroll lock while the player is open

  React.useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // On iOS Safari a `fixed inset-0` overlay doesn't always cover the bottom
    // safe-area when the toolbar is in its floating state, so the (white) page
    // background peeks through as a thin sliver below the card — most visible
    // against a dark/full-bleed video. Paint the body + html black while the
    // player is open so anything that shows through matches the backdrop.
    const originalBodyBg = document.body.style.backgroundColor;
    const originalHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = "#000";
    document.documentElement.style.backgroundColor = "#000";
    return () => {
      document.body.style.overflow = original;
      document.body.style.backgroundColor = originalBodyBg;
      document.documentElement.style.backgroundColor = originalHtmlBg;
    };
  }, []);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Completion submission when the user reaches the final slide.

  const completionSubmittedRef = React.useRef(false);
  // Tracks how many times we've already attempted (and failed) the
  // completion call for this arrival on the completion screen. Used to
  // gate the auto-retry: one retry, then surface a hard error so the
  // user knows their progress wasn't saved instead of silently sitting
  // on the celebration screen.
  const completionRetryCountRef = React.useRef(0);

  // Mirror the dynamic values the submit handler needs into refs so the
  // submit effect can depend ONLY on `isOnCompletionScreen`. With
  // cards / responsesByCardUuid / submitCompletion in the effect deps
  // (their identities change on every optimistic response update),
  // every render fired the cleanup, killed the in-flight retry timer,
  // and the auto-retry never landed.
  const cardsRef = React.useRef(cards);
  const responsesByCardUuidRef = React.useRef(responsesByCardUuid);
  const submitCompletionRef = React.useRef(submitCompletion);
  const onCompletedRef = React.useRef(onCompleted);
  const emblaApiRef = React.useRef(emblaApi);
  React.useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);
  React.useEffect(() => {
    responsesByCardUuidRef.current = responsesByCardUuid;
  }, [responsesByCardUuid]);
  React.useEffect(() => {
    submitCompletionRef.current = submitCompletion;
  }, [submitCompletion]);
  React.useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);
  React.useEffect(() => {
    emblaApiRef.current = emblaApi;
  }, [emblaApi]);

  // Localized toast strings. Inline rather than threading the cardCommon
  // helpers — these are player-shell strings, not card content.
  const tOuter = React.useCallback(
    (en, es, zh) => {
      if (language === "spanish") return es;
      if (language === "chinese") return zh;
      return en;
    },
    [language],
  );
  const tOuterRef = React.useRef(tOuter);
  React.useEffect(() => {
    tOuterRef.current = tOuter;
  }, [tOuter]);

  React.useEffect(() => {
    if (!isOnCompletionScreen) {
      completionSubmittedRef.current = false;
      completionRetryCountRef.current = 0;
      return;
    }
    if (completionSubmittedRef.current) return;
    completionSubmittedRef.current = true;

    let retryTimer = null;
    let cancelled = false;

    const attemptSubmit = () => {
      submitCompletionRef.current()
        .then((result) => {
          if (cancelled) return;
          if (result && onCompletedRef.current) onCompletedRef.current(result);
        })
        .catch((err) => {
          // Surface failure even if the player has already unmounted —
          // toasts live at the app root, so the user still sees the
          // message after closing. Without this, a learner who taps
          // Continue while the submit is mid-flight could leave
          // believing completion saved when the network call rejected
          // moments later. We skip in-player UI work (embla scroll,
          // auto-retry) when cancelled, but the toast always fires.
          const t = tOuterRef.current;
          // Distinguish failure modes by HTTP status:
          //   - 4xx (validation): the server says something about the
          //     submission itself is wrong — most often an unanswered
          //     interactive card. Bounce the user to it. If the local
          //     responses map already shows everything answered, the
          //     state is stale relative to the server; surface a hard
          //     error and let the user reopen rather than retry-loop.
          //   - 5xx / network / no status: transient. Auto-retry once
          //     while still mounted, then hard-error so the user knows
          //     nothing saved.
          const status = err && err.response && err.response.status;
          const isValidationError =
            typeof status === "number" && status >= 400 && status < 500;
          if (isValidationError) {
            const firstUnansweredIdx = cardsRef.current.findIndex(
              (c) =>
                (c.card_type === "poll" || c.card_type === "quiz") &&
                !responsesByCardUuidRef.current[c.uuid],
            );
            if (
              !cancelled &&
              firstUnansweredIdx >= 0 &&
              emblaApiRef.current
            ) {
              emblaApiRef.current.scrollTo(firstUnansweredIdx);
              completionSubmittedRef.current = false;
            } else {
              toast.error(
                t(
                  "Couldn't save your progress. Please close and reopen to try again.",
                  "No se pudo guardar tu progreso. Cierra y vuelve a abrir para intentarlo de nuevo.",
                  "无法保存进度。请关闭后重新打开重试。",
                ),
              );
              completionSubmittedRef.current = false;
            }
            return;
          }
          completionRetryCountRef.current += 1;
          if (completionRetryCountRef.current === 1 && !cancelled) {
            // First transient failure while still mounted: tell the
            // user we're retrying, schedule the retry. If they close
            // before this fires, the cleanup clears the timer and the
            // user gets the hard-error toast on the next failure path
            // (or this one, if the close happened mid-fetch).
            toast.error(
              t(
                "Couldn't save your progress. Retrying…",
                "No se pudo guardar tu progreso. Reintentando…",
                "无法保存进度。正在重试…",
              ),
            );
            retryTimer = setTimeout(() => {
              if (cancelled) return;
              attemptSubmit();
            }, 2000);
          } else {
            // Either we already retried once, or the player closed
            // mid-flight. Surface the hard error so the user knows
            // nothing saved — even if they've already left the player.
            toast.error(
              t(
                "Couldn't save your progress. Please close and reopen to try again.",
                "No se pudo guardar tu progreso. Cierra y vuelve a abrir para intentarlo de nuevo.",
                "无法保存进度。请关闭后重新打开重试。",
              ),
            );
            completionSubmittedRef.current = false;
          }
        });
    };

    attemptSubmit();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isOnCompletionScreen]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Render states

  // Backdrop stays mounted across loading/error/main so we don't get a
  // visual flash from re-mounting the fixed overlay (or from
  // motion.div's `initial: opacity 0` fading the whole backdrop in).
  // The fade-in is applied only to the phone frame in the main render.
  const backdropClasses =
    "fixed inset-0 z-[1400] bg-black/95 flex items-center justify-center";

  if (isLoading) {
    return (
      <div className={backdropClasses}>
        {/* Escape hatch — without this the user has no way out if the fetch
            hangs (silent network stall, or a required prop that never
            resolves and leaves the early-return branch of fetchDeck with
            isLoading stuck at true). Matches the top-chrome X styling
            used in the main render. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close deck"
          className="absolute top-3 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
        >
          <CloseIcon style={{ fontSize: 18 }} />
        </button>
        <div className="text-white/80 text-sm">Loading…</div>
      </div>
    );
  }

  if (error || !deck) {
    return (
      <div className={`${backdropClasses} px-8`}>
        <div className="text-center">
          <div className="text-white/90 text-base mb-4">
            We couldn't load this deck.
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white underline text-sm"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Card renderer

  const renderCard = (card, index) => {
    const isActive = index === selectedIndex;
    const isNearSelectedCard = Math.abs(index - selectedIndex) <= 1;
    const hasRenderableMediaUrl = !!card.media?.url;
    // If a signed URL is past our refresh threshold, keep the already
    // fetched URL mounted while refreshCardMedia retries. S3 URLs are
    // still usually valid between the 45-minute refresh threshold and
    // one-hour expiry, and showing stale media is better than blanking
    // the active slide on a transient refresh failure.
    const shouldLoadMedia =
      isNearSelectedCard &&
      (!isCardMediaStale(card.uuid) || hasRenderableMediaUrl);
    const existingResponse = responsesByCardUuid[card.uuid];
    const onSubmitForCard = (selectedIndices) =>
      submitResponse(card.uuid, selectedIndices);

    switch (card.card_type) {
      case "image_text":
        return (
          <ImageTextCard
            card={card}
            isActive={isActive}
            shouldLoadMedia={shouldLoadMedia}
            onAdvance={handleAdvance}
          />
        );
      case "video":
        // A video card is EITHER an uploaded clip (native <video>) or a
        // YouTube/Vimeo embed (provider SDK player). Route here so neither
        // component carries conditional hooks.
        if (card.content?.embed_provider && card.content?.embed_video_id) {
          return (
            <VideoEmbedCard
              card={card}
              isActive={isActive}
              // Cap concurrent YouTube players at 2 (active + next): YouTube
              // can't initialize 3 embedded players at once (the 3rd hangs).
              // Preloading only the NEXT card keeps forward swipes instant.
              isNextCard={index === selectedIndex + 1}
              onAdvance={handleAdvance}
              isMuted={isMuted}
              onToggleMute={handleToggleMute}
              onProgress={isActive ? setVideoProgress : undefined}
            />
          );
        }
        return (
          <VideoCard
            card={card}
            isActive={isActive}
            shouldLoadMedia={shouldLoadMedia}
            onAdvance={handleAdvance}
            isMuted={isMuted}
            onToggleMute={handleToggleMute}
            onProgress={isActive ? setVideoProgress : undefined}
          />
        );
      case "poll":
        return (
          <PollCard
            card={card}
            isActive={isActive}
            shouldLoadMedia={shouldLoadMedia}
            existingResponse={existingResponse}
            onAdvance={handleAdvance}
            onSubmitResponse={onSubmitForCard}
            language={language}
          />
        );
      case "quiz":
        return (
          <TriviaCard
            card={card}
            isActive={isActive}
            shouldLoadMedia={shouldLoadMedia}
            existingResponse={existingResponse}
            onAdvance={handleAdvance}
            onSubmitResponse={onSubmitForCard}
            language={language}
          />
        );
      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-black text-white text-sm opacity-60">
            Unsupported card type
          </div>
        );
    }
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Main render

  return (
    <div className={backdropClasses}>
      {/* Phone-frame on desktop, edge-to-edge on mobile. This codebase uses
          desktop-first Tailwind breakpoints (max-width-based), so the
          unprefixed classes apply to desktop and `sm:` overrides apply on
          mobile (≤640px). Only the frame fades in — the backdrop is stable
          across loading/main so we don't flash through to the modal behind. */}
      <motion.div
        id="swipe-deck-frame"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="relative h-[90vh] max-h-[900px] aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-2xl sm:h-full sm:max-h-none sm:aspect-auto sm:w-full sm:rounded-none"
      >
        {/* Top chrome — dark gradient backdrop ensures the white progress
            bar + close X stay visible against any card background, including
            the white trivia/poll cards that have no media to provide contrast. */}
        <div
          className="absolute top-0 left-0 right-0 z-20 px-4 pt-3 pb-3 flex items-center gap-2"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)",
          }}
        >
          <div className="flex-1 flex gap-1">
            {/* One chunk per slide: cards + the completion screen. Past slides
                fill fully; the active slide fills in real time while a video
                plays (so its length is visible, Stories-style) and otherwise
                stays full; future slides are empty. */}
            {Array.from({ length: totalCards + 1 }).map((_, i) => {
              const activeCard = cards[selectedIndex];
              const activeIsVideo = activeCard?.card_type === "video";
              // Embeds report progress via a coarse poll (~500ms) instead of the
              // native <video>'s frequent timeupdate, so the active segment uses a
              // longer ease-linear transition to glide between polls (1x playback
              // keeps the average rate correct) rather than stepping every 500ms.
              const activeIsEmbed =
                activeIsVideo && Boolean(activeCard?.content?.embed_provider);
              let fillPct = 0;
              if (i < selectedIndex) {
                fillPct = 100;
              } else if (i === selectedIndex) {
                fillPct = activeIsVideo ? videoProgress * 100 : 100;
              }
              const isActiveVideoSegment =
                i === selectedIndex && activeIsVideo;
              return (
                <div
                  key={i}
                  className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden"
                >
                  <div
                    className={`h-full bg-white ${
                      isActiveVideoSegment
                        ? activeIsEmbed
                          ? "transition-[width] duration-500 ease-linear"
                          : "transition-[width] duration-150 ease-linear"
                        : "transition-all duration-300"
                    }`}
                    style={{ width: `${fillPct}%` }}
                  />
                </div>
              );
            })}
          </div>
          {!isPublic && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close deck"
              className="ml-2 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          )}
        </div>

        {/* Embla viewport — N card slides + 1 completion slide */}
        <div className="absolute inset-0 overflow-hidden" ref={emblaRef}>
          <div className="flex flex-col h-full">
            {cards.map((card, i) => (
              <div
                key={card.uuid}
                className="flex-shrink-0 w-full h-full min-h-0"
              >
                {renderCard(card, i)}
              </div>
            ))}
            <div className="flex-shrink-0 w-full h-full min-h-0">
              <CompletionScreen
                deck={deck}
                cards={cards}
                responsesByCardUuid={responsesByCardUuid}
                language={language}
                isActive={isOnCompletionScreen}
                isPublic={isPublic}
                onContinue={onClose}
                onReplay={() => {
                  if (emblaApi) emblaApi.scrollTo(0);
                }}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
